import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, RefreshCw, Zap, FileText } from "lucide-react";
import { api } from "../../lib/api";
import StatusDot from "./StatusDot";
import ChapterSourceModal from "./ChapterSourceModal";

const KINDS = ["lesson", "flashcards", "quiz", "scenario", "summary"];

export default function ContentTab() {
  const [rows, setRows] = useState([]);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [sourceChapterId, setSourceChapterId] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/content-status");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const pollJob = async (jobId) => {
    const r = await api.get(`/admin/generate/${jobId}`);
    setJob(r.data);
    if (r.data.status === "running" || r.data.status === "queued") {
      setTimeout(() => pollJob(jobId), 2500);
    } else {
      setRunning(false);
      load();
    }
  };

  const startAll = async (overwrite = false) => {
    setRunning(true); setError("");
    try {
      const r = await api.post("/admin/generate", { include_arabic: true, overwrite });
      setJob({ job_id: r.data.job_id, status: "queued", done: 0, total: 0 });
      pollJob(r.data.job_id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to start");
      setRunning(false);
    }
  };

  const cancelAll = async () => {
    try {
      await api.post("/admin/generate/cancel-all");
      setRunning(false);
      setJob((j) => (j ? { ...j, status: "cancelled" } : j));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to cancel");
    }
  };

  const startChapter = async (cid) => {
    setRunning(true); setError("");
    try {
      const r = await api.post("/admin/generate", { chapter_id: cid, include_arabic: true });
      setJob({ job_id: r.data.job_id, status: "queued", done: 0, total: 0 });
      pollJob(r.data.job_id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to start");
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-border rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-lg font-semibold">Pre-generate all content</div>
          <div className="text-sm text-muted-foreground">English + Arabic, cached in the database. Students load instantly.</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => startAll(false)}
            disabled={running}
            data-testid="generate-all-button"
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-xl transition-all"
          >
            <Zap className="w-4 h-4" /> {running ? "Running…" : "Generate missing"}
          </button>
          <button
            onClick={() => startAll(true)}
            disabled={running}
            className="inline-flex items-center gap-2 bg-white border border-border hover:bg-secondary disabled:opacity-40 px-4 py-2.5 rounded-xl transition-all text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Regenerate all
          </button>
          <button
            onClick={cancelAll}
            data-testid="cancel-all-button"
            className="inline-flex items-center gap-2 bg-white border border-terracotta/40 text-terracotta hover:bg-terracotta/5 px-4 py-2.5 rounded-xl transition-all text-sm"
          >
            Cancel
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-terracotta/10 border border-terracotta/30 text-sm text-terracotta flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {job && (
        <div className="bg-white border border-border rounded-2xl p-5" data-testid="generation-job">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">Job {job.job_id} · <span className="text-muted-foreground">{job.status}</span></div>
            <div className="text-sm font-mono">{job.done}/{job.total}</div>
          </div>
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-sage transition-all" style={{ width: job.total ? `${(job.done / job.total) * 100}%` : "0%" }} />
          </div>
          {job.last_error && <div className="mt-3 text-xs text-terracotta">{job.last_error}</div>}
        </div>
      )}

      <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="content-status">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-4 py-3">Chapter</th>
              {KINDS.map((k) => <th key={k} className="px-2 py-3 text-center">{k}</th>)}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.chapter.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium text-xs uppercase tracking-wider text-muted-foreground">Ch {row.chapter.number}</div>
                  <div className="text-sm">{row.chapter.title}</div>
                </td>
                {KINDS.map((k) => (
                  <td key={k} className="px-2 py-3 text-center">
                    <div className="inline-flex flex-col items-center gap-0.5">
                      <StatusDot on={row.content[k].en} label="EN" />
                      <StatusDot on={row.content[k].ar} label="AR" />
                    </div>
                  </td>
                ))}
                <td className="px-4 py-3 text-end">
                  <div className="inline-flex gap-1.5">
                    <button
                      onClick={() => setSourceChapterId(row.chapter.id)}
                      data-testid={`source-${row.chapter.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-secondary text-xs font-medium"
                      title="View / edit the source text used by the LLM for this chapter"
                    >
                      <FileText className="w-3.5 h-3.5" /> Source
                    </button>
                    <button
                      onClick={() => startChapter(row.chapter.id)}
                      disabled={running}
                      data-testid={`generate-${row.chapter.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-secondary disabled:opacity-40 text-xs font-medium"
                    >
                      <Zap className="w-3.5 h-3.5" /> Generate
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HeroImagesSection />

      {sourceChapterId && (
        <ChapterSourceModal
          chapterId={sourceChapterId}
          onClose={() => {
            setSourceChapterId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function HeroImagesSection() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState({});
  const [bulk, setBulk] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/hero-status");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const genOne = async (cid) => {
    setBusy((b) => ({ ...b, [cid]: true }));
    try {
      await api.post(`/admin/hero/${cid}`);
      await load();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[cid]; return n; });
    }
  };

  const genMissing = async () => {
    setBulk(true);
    try {
      await api.post("/admin/hero-generate-all");
      // Poll every 5s for up to 3 minutes
      let done = false;
      for (let i = 0; i < 36 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const r = await api.get("/admin/hero-status");
        setRows(r.data);
        done = r.data.every((x) => x.generated);
      }
    } finally { setBulk(false); }
  };

  const missingCount = rows.filter((r) => !r.generated).length;

  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="hero-images-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-display text-lg font-semibold">Chapter illustrations</div>
          <div className="text-sm text-muted-foreground">AI-generated hero images (Gemini Nano Banana) shown above each lesson.</div>
        </div>
        <button
          onClick={genMissing}
          disabled={bulk || missingCount === 0}
          data-testid="gen-missing-heros"
          className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-40 text-white font-medium px-4 py-2 rounded-xl transition-all text-sm"
        >
          <Zap className="w-4 h-4" /> {bulk ? "Generating…" : missingCount ? `Generate ${missingCount} missing` : "All generated"}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((r) => (
          <div key={r.chapter_id} className="border border-border rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`hero-row-${r.chapter_id}`}>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {r.chapter_id.toUpperCase()}
              </div>
              <div className="text-xs mt-1">
                {r.generated ? (
                  <span className="inline-flex items-center gap-1.5 text-sage font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> ready
                  </span>
                ) : (
                  <span className="text-muted-foreground">not generated</span>
                )}
              </div>
            </div>
            <button
              onClick={() => genOne(r.chapter_id)}
              disabled={!!busy[r.chapter_id] || bulk}
              data-testid={`gen-hero-${r.chapter_id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-secondary disabled:opacity-40 text-xs font-medium"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy[r.chapter_id] ? "animate-spin" : ""}`} />
              {busy[r.chapter_id] ? "…" : r.generated ? "Regenerate" : "Generate"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
